"use client";

import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { COHENS_KAPPA_CALCULATOR_STRINGS } from "@/components/tools/cohens-kappa-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type AgreementBand,
  cohenKappa,
  type KappaResult,
  type KappaWeighting,
  landisKoch,
} from "@/lib/tool-kit/stats-research/agreement";
import {
  apaBounded,
  apaPClause,
  type Formatter,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";

/*
 * The result panel of the Cohen's kappa calculator: headline kappa, the weighted
 * variants, and the working. Strings are the widget's own, passed in.
 */

type Strings = typeof COHENS_KAPPA_CALCULATOR_STRINGS;

const BAND_KEY: Record<AgreementBand, keyof Strings> = {
  poor: "bandPoor",
  slight: "bandSlight",
  fair: "bandFair",
  moderate: "bandModerate",
  substantial: "bandSubstantial",
  almostPerfect: "bandAlmostPerfect",
};

const WEIGHT_KEY: Record<KappaWeighting, keyof Strings> = {
  none: "weightsNone",
  linear: "weightsLinear",
  quadratic: "weightsQuadratic",
};

function WeightedTable({
  rows,
  strings,
  fmt,
}: {
  rows: [KappaWeighting, KappaResult][];
  strings: Strings;
  fmt: Formatter;
}) {
  const three = (value: number) => fixed(fmt, value, 3);
  const head = [
    strings.columnWeights,
    strings.columnKappa,
    strings.columnSe,
    strings.columnInterval,
    strings.columnZ,
    strings.columnP,
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([weighting, result]) => (
            <tr key={weighting}>
              <th className={`${TD} font-medium`} scope="row">
                {strings[WEIGHT_KEY[weighting]]}
              </th>
              <td className={`${TD} font-mono`}>{three(result.kappa)}</td>
              <td className={`${TD} font-mono`}>{three(result.se)}</td>
              <td className={`${TD} font-mono`}>
                [{three(result.lower)}, {three(result.upper)}]
              </td>
              <td className={`${TD} font-mono`}>{fixed(fmt, result.z, 2)}</td>
              <td className={`${TD} font-mono`}>{formatP(fmt, result.p)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Working({
  table,
  result,
  strings,
  fmt,
}: {
  table: number[][];
  result: KappaResult;
  strings: Strings;
  fmt: Formatter;
}) {
  const four = (value: number) => fixed(fmt, value, 4);
  let agreements = 0;
  let products = 0;
  for (const [i, row] of table.entries()) {
    agreements += row[i];
    const rowTotal = row.reduce((sum, value) => sum + value, 0);
    const columnTotal = table.reduce((sum, other) => sum + other[i], 0);
    products += rowTotal * columnTotal;
  }
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingObserved, {
          agreements: fmt(agreements),
          n: fmt(result.n),
          observed: four(result.observed),
        })}
      </p>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingExpected, {
          products: fmt(products),
          nSquared: fmt(result.n * result.n),
          expected: four(result.expected),
        })}
      </p>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingKappa, {
          observed: four(result.observed),
          expected: four(result.expected),
          kappa: fixed(fmt, result.kappa, 3),
        })}
      </p>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingSe, { seNull: fixed(fmt, result.seNull, 4) })}
      </p>
    </div>
  );
}

export function KappaSummary({
  table,
  strings,
  fmt,
}: {
  table: number[][];
  strings: Strings;
  fmt: Formatter;
}) {
  const simple = cohenKappa(table);
  if (!simple) {
    return <Notice>{strings.degenerate}</Notice>;
  }
  const three = (value: number) => fixed(fmt, value, 3);
  const weighted: [KappaWeighting, KappaResult][] = [];
  if (table.length >= 3) {
    for (const weighting of ["none", "linear", "quadratic"] as const) {
      const result = cohenKappa(table, weighting);
      if (result) {
        weighted.push([weighting, result]);
      }
    }
  }
  const report = t(strings.report, {
    kappa: apaBounded(fmt, simple.kappa),
    lower: apaBounded(fmt, simple.lower),
    upper: apaBounded(fmt, simple.upper),
    z: fixed(fmt, simple.z, 2),
    p: apaPClause(fmt, simple.p),
    n: fmt(simple.n),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.kappa} value={three(simple.kappa)} />
        <StatCard
          label={strings.interval}
          value={`[${three(simple.lower)}, ${three(simple.upper)}]`}
        />
        <StatCard label={strings.observed} value={three(simple.observed)} />
        <StatCard label={strings.expected} value={three(simple.expected)} />
        <StatCard label={strings.zLabel} value={fixed(fmt, simple.z, 2)} />
        <StatCard label={strings.pLabel} value={formatP(fmt, simple.p)} />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings.interpretation, {
          kappa: three(simple.kappa),
          band: strings[BAND_KEY[landisKoch(simple.kappa)]],
        })}{" "}
        {simple.maxKappa === null
          ? null
          : t(strings.maxKappa, { value: three(simple.maxKappa) })}{" "}
        {strings.caveat}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <p className="font-mono text-muted-foreground text-sm">{report}</p>
      </div>
      {weighted.length > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.weightedHeading}</h3>
          <WeightedTable fmt={fmt} rows={weighted} strings={strings} />
          <p className="text-muted-foreground text-xs">
            {strings.weightedNote}
          </p>
        </div>
      ) : null}
      <Working fmt={fmt} result={simple} strings={strings} table={table} />
    </section>
  );
}
